import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Users, Heart, AlertCircle, Archive, Dumbbell, Megaphone,
  Settings, Eye, Filter, Check
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ClearanceBadge from '../components/ClearanceBadge';
import { timeAgo } from '../utils/relativeTime';

const API = process.env.REACT_APP_BACKEND_URL;

const SQUAD_ORDER = ['Senior', 'U19', 'U16', 'U14', 'U12'];

const TeamState = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [inactiveAthletes, setInactiveAthletes] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagMap, setTagMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [selectedSquad, setSelectedSquad] = useState(null); // null = show all
  const [activeTagIds, setActiveTagIds] = useState([]);
  const [smartFilter, setSmartFilter] = useState(null); // 'injured' | 'no-wellness' | 'missed-last'
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      try {
        const [ct, tg, mp] = await Promise.all([
          axios.get(`${API}/api/dashboard/coach-today`, h),
          axios.get(`${API}/api/tags`, h).catch(() => ({ data: [] })),
          axios.get(`${API}/api/tags/map`, h).catch(() => ({ data: {} })),
        ]);
        setToday(ct.data);
        setTags(tg.data || []);
        setTagMap(mp.data || {});
      } catch {
        toast.error('Failed to load team state');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!showInactive || inactiveAthletes.length) return;
    const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    axios.get(`${API}/api/athletes?status=inactive`, h)
      .then(r => setInactiveAthletes(r.data || []))
      .catch(() => {});
  }, [showInactive, inactiveAthletes.length]);

  // Build per-squad structure
  const squads = useMemo(() => {
    if (!today) return [];
    const bySquad = {};
    const activeRows = today.athlete_rows || [];
    activeRows.forEach(r => {
      const key = r.squad || 'Unassigned';
      (bySquad[key] = bySquad[key] || []).push({ ...r, _inactive: false });
    });
    if (showInactive) {
      inactiveAthletes.forEach(a => {
        const key = a.squad || 'Unassigned';
        (bySquad[key] = bySquad[key] || []).push({
          id: a.id,
          full_name: a.full_name,
          squad: a.squad,
          position: a.position,
          tag_ids: tagMap[a.id] || [],
          availability: 'Inactive',
          last_wellness: null,
          wellness_today: false,
          last_match: null,
          missed_last_session: false,
          pdp_overdue: false,
          _inactive: true,
        });
      });
    }

    // Sort squads: preferred order first, then others alphabetically, Unassigned last
    const keys = Object.keys(bySquad);
    const preferred = SQUAD_ORDER.filter(k => keys.includes(k));
    const others = keys.filter(k => !SQUAD_ORDER.includes(k) && k !== 'Unassigned').sort();
    const result = [...preferred, ...others];
    if (keys.includes('Unassigned')) result.push('Unassigned');

    return result.map(key => {
      const rows = bySquad[key];
      const active = rows.filter(r => !r._inactive);
      const inactive = rows.filter(r => r._inactive);
      return {
        name: key,
        rows,
        total_active: active.length,
        injured: active.filter(r => r.availability === 'Injured').length,
        available: active.filter(r => r.availability !== 'Injured').length,
        no_wellness: active.filter(r => r.wellness_today === false).length,
        inactive_count: inactive.length,
      };
    });
  }, [today, inactiveAthletes, showInactive, tagMap]);

  // Apply active filters to compute the "current training group" per squad
  const filterRow = (r) => {
    if (r._inactive && !showInactive) return false;
    if (smartFilter === 'injured' && r.availability !== 'Injured') return false;
    if (smartFilter === 'no-wellness' && r.wellness_today !== false) return false;
    if (smartFilter === 'missed-last' && !r.missed_last_session) return false;
    if (smartFilter === 'pdp-overdue' && !r.pdp_overdue) return false;
    if (activeTagIds.length) {
      const rowTags = r.tag_ids || [];
      if (!activeTagIds.some(t => rowTags.includes(t))) return false;
    }
    return true;
  };

  const renderAthleteRow = (r) => {
    const tagChips = (r.tag_ids || []).slice(0, 3).map(tid => tags.find(t => t.id === tid)).filter(Boolean);
    return (
      <div
        key={r.id}
        className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
          r._inactive
            ? 'bg-gray-50/60 dark:bg-gray-800/40 border-gray-100 dark:border-gray-700 opacity-70'
            : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:border-pfap-green/40'
        }`}
        data-testid={`team-state-row-${r.id}`}
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pfap-green/20 to-pfap-green/5 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-pfap-green">
          {(r.full_name || '??').split(' ').map(p => p[0]).slice(0,2).join('')}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{r.full_name}</p>
            <span className="text-[10px] text-gray-400">{r.position}</span>
            {!r._inactive && (
              <ClearanceBadge value={r.availability} testId={`ts-clearance-${r.id}`} />
            )}
            {r._inactive && (
              <span className="text-[9px] px-1.5 py-0 rounded-full bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-300">Inactive</span>
            )}
            {r.wellness_today === false && !r._inactive && (
              <span className="text-[9px] px-1.5 py-0 rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300">No wellness</span>
            )}
            {r.missed_last_session && !r._inactive && (
              <span className="text-[9px] px-1.5 py-0 rounded-full bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-300">Missed last</span>
            )}
            {r.last_wellness?.date ? (
              <span className="text-[9px] text-gray-400" data-testid={`ts-last-wellness-${r.id}`} title="Last wellness check-in">
                wellness {timeAgo(r.last_wellness.date)}
              </span>
            ) : null}
          </div>
          {tagChips.length > 0 && (
            <div className="flex gap-1 mt-0.5">
              {tagChips.map(t => (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0 rounded-full border"
                  style={{ borderColor: (t.color || '#64748b') + '66', color: t.color || '#475569', background: (t.color || '#64748b') + '14' }}
                >
                  <span className="w-1 h-1 rounded-full" style={{ background: t.color || '#64748b' }} />
                  {t.name}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-0.5 opacity-80">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Open 360°" onClick={() => navigate(`/athlete/${r.id}/360`)} data-testid={`ts-open-360-${r.id}`}>
            <Eye size={13} />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Manage" onClick={() => navigate(`/athletes/${r.id}/manage`)} data-testid={`ts-manage-${r.id}`}>
            <Settings size={13} />
          </Button>
        </div>
      </div>
    );
  };

  const renderSquadCard = (s) => {
    const visible = s.rows.filter(filterRow);
    const filtered = activeTagIds.length > 0 || smartFilter;
    return (
      <Card key={s.name} className="bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700" data-testid={`team-state-squad-${s.name}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base text-gray-900 dark:text-gray-100 flex items-center gap-2">
                {s.name}
                {filtered && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid={`ts-group-count-${s.name}`}>
                    Group: {visible.length}
                  </Badge>
                )}
              </CardTitle>
              <p className="text-[11px] text-gray-400 mt-0.5">Active roster · {s.total_active}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[11px]"
                onClick={() => navigate('/training')}
                title="Assign training"
                data-testid={`ts-assign-training-${s.name}`}
              >
                <Dumbbell size={11} className="mr-1" /> Training
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[11px]"
                onClick={() => navigate(`/announcements?targetSquad=${encodeURIComponent(s.name)}`)}
                title="Send announcement"
                data-testid={`ts-announce-${s.name}`}
              >
                <Megaphone size={11} className="mr-1" /> Notify
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-1.5 mt-3">
            <Mini label="Available" value={s.available} color="text-emerald-600" testId={`ts-mini-available-${s.name}`} />
            <Mini label="Injured" value={s.injured} color={s.injured ? 'text-red-600' : 'text-gray-400'} testId={`ts-mini-injured-${s.name}`} />
            <Mini label="No wellness" value={s.no_wellness} color={s.no_wellness ? 'text-amber-600' : 'text-gray-400'} testId={`ts-mini-nowell-${s.name}`} />
            <Mini label="Inactive" value={s.inactive_count} color={s.inactive_count ? 'text-gray-500' : 'text-gray-300'} testId={`ts-mini-inactive-${s.name}`} />
          </div>
        </CardHeader>
        <CardContent className="pt-0 pb-4">
          {visible.length === 0 ? (
            <div className="py-6 text-center text-xs text-gray-400 dark:text-gray-500" data-testid={`ts-empty-${s.name}`}>
              {filtered ? 'No athletes match the current filters.' : 'No athletes in this squad.'}
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1" data-testid={`ts-squad-list-${s.name}`}>
              {visible.map(renderAthleteRow)}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const visibleSquads = selectedSquad ? squads.filter(s => s.name === selectedSquad) : squads;

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="team-state-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Team State
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Real-time squad composition and training group · pulled from canonical data only
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 select-none" data-testid="ts-show-inactive-toggle">
              <Switch checked={showInactive} onCheckedChange={setShowInactive} />
              Show inactive
            </label>
          </div>
        </div>

        {/* Squad filter */}
        <div className="flex flex-wrap items-center gap-1.5" data-testid="ts-squad-filter">
          <button
            onClick={() => setSelectedSquad(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
              !selectedSquad ? 'bg-pfap-green text-white border-pfap-green' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
            }`}
            data-testid="ts-squad-all"
          >
            All Squads
          </button>
          {squads.map(s => (
            <button
              key={s.name}
              onClick={() => setSelectedSquad(s.name)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                selectedSquad === s.name ? 'bg-pfap-green text-white border-pfap-green' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
              }`}
              data-testid={`ts-squad-pill-${s.name}`}
            >
              {s.name}
              <span className="ml-1.5 text-[10px] opacity-70">{s.total_active}</span>
            </button>
          ))}
        </div>

        {/* Smart + tag filters */}
        <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-2" data-testid="ts-dynamic-filters">
          <div className="flex items-center gap-2">
            <Filter size={13} className="text-gray-400" />
            <span className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">Dynamic group filters</span>
            {(activeTagIds.length > 0 || smartFilter) && (
              <button
                onClick={() => { setActiveTagIds([]); setSmartFilter(null); }}
                className="ml-auto text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                data-testid="ts-clear-filters"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { key: 'injured', label: 'Injured only' },
              { key: 'no-wellness', label: 'No wellness today' },
              { key: 'missed-last', label: 'Missed last session' },
              { key: 'pdp-overdue', label: 'PDP overdue' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setSmartFilter(prev => prev === f.key ? null : f.key)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${
                  smartFilter === f.key
                    ? 'bg-red-500 text-white border-red-500'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                }`}
                data-testid={`ts-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
            {tags.length > 0 && <span className="mx-2 text-gray-300">|</span>}
            {tags.map(t => {
              const on = activeTagIds.includes(t.id);
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTagIds(prev => on ? prev.filter(id => id !== t.id) : [...prev, t.id])}
                  className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border transition ${on ? 'text-white shadow-sm' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}
                  style={on ? { background: t.color || '#10b981', borderColor: t.color || '#10b981' } : { borderColor: (t.color || '#64748b') + '66' }}
                  data-testid={`ts-tag-filter-${t.id}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: on ? '#fff' : (t.color || '#64748b') }} />
                  {t.name}
                  {on && <Check size={10} />}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-[320px] rounded-xl bg-gray-100 dark:bg-gray-800/40 animate-pulse" />)}
          </div>
        ) : squads.length === 0 ? (
          <Card>
            <CardContent className="py-14 text-center text-sm text-gray-500">
              <Users size={28} className="mx-auto mb-2 opacity-40" />
              No active athletes in any squad.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ts-squads-grid">
            {visibleSquads.map(renderSquadCard)}
          </div>
        )}
      </div>
    </Layout>
  );
};

const Mini = ({ label, value, color, testId }) => (
  <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 px-2 py-1" data-testid={testId}>
    <p className={`text-base font-bold ${color}`}>{value}</p>
    <p className="text-[9px] uppercase tracking-wide text-gray-400">{label}</p>
  </div>
);

export default TeamState;
